/**
 * Trader Opportunities Page
 *
 * Displays opportunities for a specific trader at /user/:trader_name/opportunities
 * Same as the main dashboard (/) but executes strategies for the specified trader.
 */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Typography, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { type RootState } from '../app/store';
import { ConnectionStatus } from '../components/ConnectionStatus/ConnectionStatus';
import { HeaderStats } from '../components/HeaderStats';
import OpportunityTable from '../components/OpportunityTable/OpportunityTable';
import SettingsDialog from '../components/SettingsDialog/SettingsDialog';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useWebSocketConnection } from '../hooks/useWebSocketConnection';
import { useOpportunities } from '../contexts/OpportunitiesContext';
import { TraderNameProvider } from '../contexts/TraderNameContext';
import {
  openSettingsDialog,
  closeSettingsDialog,
  updateSettings,
  resetSettings
} from '../features/settings/settingsSlice';
import { selectNetworkStatus } from '../features/networkStatus/networkStatusSlice';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import {
  hasNetworkStatusData,
  getArbitrageTransferStatus
} from '../utils/networkCompatibility';
import { FeeStructure } from '../models/FeeStructure';
import { convertVolumeToUSDT } from '../utils/currencyConversion';
import {
  buildTraderDashboardPath,
  getTraderRouteVersion,
} from '../utils/traderRouting';

function TraderOpportunitiesContent() {
  const { trader_name } = useParams<{ trader_name: string }>();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { settings, isDialogOpen } = useSelector((state: RootState) => state.settings);
  const traderRouteVersion = getTraderRouteVersion(location.pathname);

  const { isConnected } = useWebSocketConnection();
  const { sortedOpportunities: allOpportunities } = useOpportunities();

  const networkStatus = useSelector(selectNetworkStatus);
  const { isAnyStale } = useNetworkStatus();

  React.useEffect(() => {
    FeeStructure.setCustomFees(settings.feeOverrides);
  }, [settings.feeOverrides]);

  const filteredOpportunities = allOpportunities.filter(opp => {
    if (opp.isStarred) return true;
    if (settings.filters.excludedTokens.includes(opp.base)) return false;

    const profitPercent = ((opp.profit_ratio || 1.0) - 1) * 100;
    if (profitPercent < settings.filters.minProfitPercent) return false;
    if (settings.filters.maxProfitPercent !== null && profitPercent > settings.filters.maxProfitPercent) return false;

    const volumeInQuote = (opp.max_volume || 0) * (opp.buy_price || 0);
    const volumeUSDT = convertVolumeToUSDT(volumeInQuote, opp.buy_quote);
    if (volumeUSDT < settings.filters.minVolumeUSD) return false;

    const buyEnabled = settings.buyExchanges?.[opp.buy_exchange] ?? true;
    const sellEnabled = settings.sellExchanges?.[opp.sell_exchange] ?? true;
    if (!buyEnabled || !sellEnabled) return false;

    if (settings.filters.networkStatus?.enabled && hasNetworkStatusData(networkStatus)) {
      const transferStatus = getArbitrageTransferStatus(opp, networkStatus);

      if (settings.filters.networkStatus.hideUnknown) {
        if (transferStatus.withdrawStatus === 'unknown' || transferStatus.depositStatus === 'unknown') return false;
      }
      if (settings.filters.networkStatus.hideDisabled) {
        if (transferStatus.withdrawStatus === 'disabled' || transferStatus.depositStatus === 'disabled') return false;
      }
      if (settings.filters.networkStatus.requireBothEnabled) {
        if (transferStatus.withdrawStatus !== 'enabled' || transferStatus.depositStatus !== 'enabled') return false;
      }
    }

    return true;
  });

  const sortedOpportunities = [...filteredOpportunities].sort((a, b) => {
    if (a.isStarred && !b.isStarred) return -1;
    if (!a.isStarred && b.isStarred) return 1;

    switch (settings.display.sortBy) {
      case 'profit_desc':
        return (b.profit_ratio || 1.0) - (a.profit_ratio || 1.0);
      case 'created_desc':
        return b.detected_at - a.detected_at;
      case 'updated_desc':
        return b.updated_at - a.updated_at;
      default:
        return 0;
    }
  });

  const handleOpenSettings = React.useCallback(() => dispatch(openSettingsDialog()), [dispatch]);
  const handleCloseSettings = React.useCallback(() => dispatch(closeSettingsDialog()), [dispatch]);
  const handleApplySettings = React.useCallback((newSettings: typeof settings) => dispatch(updateSettings(newSettings)), [dispatch]);
  const handleResetSettings = React.useCallback(() => dispatch(resetSettings()), [dispatch]);

  return (
    <TraderNameProvider traderName={trader_name || 'njovy'}>
      <SettingsDialog
        open={isDialogOpen}
        onClose={handleCloseSettings}
        currentSettings={settings}
        onApply={handleApplySettings}
        onReset={handleResetSettings}
      />

      <IconButton
        onClick={handleOpenSettings}
        sx={{
          position: 'fixed',
          bottom: { xs: 16, sm: 24, md: 32 },
          right: { xs: 16, sm: 24, md: 32 },
          color: 'primary.main',
          border: '2px solid',
          borderColor: 'primary.main',
          backgroundColor: 'rgba(0, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          width: { xs: 48, sm: 56 },
          height: { xs: 48, sm: 56 },
          fontSize: { xs: '1.5rem', sm: '1.75rem' },
          zIndex: 1000,
          '&:hover': {
            backgroundColor: 'rgba(0, 255, 255, 0.2)',
            boxShadow: '0 0 30px rgba(0, 255, 255, 0.6)',
            transform: 'scale(1.05)',
          },
          transition: 'all 0.3s ease',
        }}
        aria-label={t('app.settings')}
      >
        Settings
      </IconButton>

      <LanguageSwitcher />
      <ConnectionStatus />

      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #020817 0%, #0a0f1e 100%)',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              repeating-linear-gradient(
                0deg,
                transparent,
                transparent 40px,
                rgba(0, 255, 255, 0.03) 40px,
                rgba(0, 255, 255, 0.03) 41px
              ),
              repeating-linear-gradient(
                90deg,
                transparent,
                transparent 40px,
                rgba(255, 0, 255, 0.03) 40px,
                rgba(255, 0, 255, 0.03) 41px
              )
            `,
            pointerEvents: 'none',
          },
        }}
      >
        <Box sx={{
          pt: { xs: 8, sm: 6, md: 4 },
          pb: { xs: 2, sm: 3, md: 4 },
          px: 0,
          position: 'relative',
          zIndex: 1,
          width: '100%'
        }}>
          {/* Header with back button */}
          <Box sx={{ mb: { xs: 3, md: 4 }, textAlign: 'center', position: 'relative' }}>
            <IconButton
              onClick={() => navigate(buildTraderDashboardPath(trader_name || 'njovy', traderRouteVersion))}
              sx={{
                position: 'absolute',
                left: { xs: 8, sm: 16 },
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'text.secondary',
              }}
            >
              <ArrowBackIcon />
            </IconButton>

            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontSize: { xs: '1.5rem', sm: '2.75rem', md: '3.75rem' },
                fontWeight: 'bold',
                letterSpacing: { xs: '0.05em', sm: '0.1em' },
                textTransform: 'uppercase',
                background: 'linear-gradient(135deg, #00ffff 0%, #ff00ff 50%, #00ffff 100%)',
                backgroundSize: '200% auto',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 0 40px rgba(0, 255, 255, 0.8)',
                animation: 'gradient 3s ease 10',
                '@keyframes gradient': {
                  '0%': { backgroundPosition: '0% 50%' },
                  '50%': { backgroundPosition: '100% 50%' },
                  '100%': { backgroundPosition: '0% 50%' },
                },
                mb: 1,
              }}
            >
              {t('app.title').toUpperCase()}
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                fontSize: { xs: '0.65rem', sm: '0.875rem', md: '1rem' },
                color: 'primary.main',
                letterSpacing: { xs: '0.05em', sm: '0.2em' },
                textTransform: 'uppercase',
                px: { xs: 2, sm: 0 },
              }}
            >
              Trading as: <strong>{trader_name}</strong>
            </Typography>
          </Box>

          <HeaderStats />

          <OpportunityTable
            opportunities={sortedOpportunities}
            loading={!isConnected}
            networkStatusStale={isAnyStale}
            networkStatusAvailable={hasNetworkStatusData(networkStatus)}
          />
        </Box>
      </Box>
    </TraderNameProvider>
  );
}

export default TraderOpportunitiesContent;
